import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Pagination,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Card,
  CardContent,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogContent,
  DialogTitle,
  AppBar,
  Toolbar,
  Slide,
  Fade
} from '@mui/material';
import {
  Search,
  Visibility,
  Restaurant,
  LocalShipping,
  Receipt,
  LocationOn,
  Download,
  FilterList,
  ShoppingCart,
  Store,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
  Close,
  ArrowBack,
  MenuBook
} from '@mui/icons-material';
import { get_orders } from '../../services/profileService';
import { downloadInvoicePDF } from '../../services/invoiceService_new';
import dayjs from "dayjs";
import { add } from 'date-fns';



// Transition for mobile dialog
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Status colors mapping
const statusColors = {
  'pending': '#ff9800',
  'in progress': '#1976d2',
  'delivered': '#2e7d32',
  'cancelled': '#d32f2f'
};

// Status text mapping
const statusText = {
  'pending': 'Pending',
  'in progress': 'In Progress',
  'delivered': 'Delivered',
  'cancelled': 'Cancelled'
};

const ProfileOrderTable = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [currentOrder, setCurrentOrder] = useState({});
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    async function fetchOrderData() {
      try {
        let data = JSON.parse(sessionStorage.getItem('user'));
        // let send_data = {
        //   "id": parseInt(data['id'])
        // };
        const response = await get_orders();
        setTableData(response);
      } catch (error) {
        console.error("Error Occurred while getting Order Data => ", error);
      }
    }
    fetchOrderData();
  }, []);

  // Filter orders based on search term and status
  const filteredOrders = tableData.filter(order => {
    const matchesSearch = order.order_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.delivery_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.outlet_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      order.order_type?.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredOrders.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const paginatedOrders = filteredOrders.slice(startIndex, startIndex + rowsPerPage);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event, newValue) => {
    setStatusFilter(newValue);
    setPage(1);
    if (isMobile) {
      setFilterDrawerOpen(false);
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleInvoiceDownload = async (unique_string) => {
    try {
      await downloadInvoicePDF(unique_string);
    } catch (error) {
      // console.log(error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleViewDetails = (order) => {
    setCurrentOrder(order);
    setDetailsModalOpen(true);
  };

  const renderMobileOrderCard = (order, index) => (
    <Card
      key={order.id || index}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
      elevation={0}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Order Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#680000' }}>
              {order.order_number || `Order #${56789 - index}`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {order.ordered_at ? formatDate(order.ordered_at) : `Oct ${28 - index}, 2023`}
            </Typography>
          </Box>
          <Chip
            label={statusText[order.order_type?.toLowerCase()] || order.order_type || 'Pending'}
            size="small"
            sx={{
              backgroundColor: statusColors[order.order_type?.toLowerCase()] || statusColors.pending,
              color: 'white',
              fontSize: '0.65rem',
              height: 24,
              minWidth: 80,
            }}
          />
        </Box>

        {/* Outlet Info */}
        {order.outlet_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Store sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">
              {order.outlet_name}
            </Typography>
          </Box>
        )}

        {/* Items Preview */}
        {order.menu_items && order.menu_items.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
            <ShoppingCart sx={{ fontSize: 16, color: '#680000' }} />
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {order.menu_items[0].name}
              {order.menu_items[0].quantity > 1 && ` (x${order.menu_items[0].quantity})`}
              {order.menu_items.length > 1 && ` +${order.menu_items.length - 1} more`}
            </Typography>
          </Box>
        )}

        {/* Price and Expand Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: '#680000' }}>
            ${order.total_amount || (45.50 - index * 13.5).toFixed(2)}
          </Typography>
          <IconButton
            size="small"
            onClick={() => toggleOrderExpansion(order.id || index)}
            sx={{ color: '#680000' }}
          >
            {expandedOrder === (order.id || index) ? <ExpandLess /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </CardContent>

      {/* Expanded Details */}
      <Collapse in={expandedOrder === (order.id || index)} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
          {/* Order Type */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {order.order_type?.toLowerCase() === 'delivery' ? <LocalShipping /> : <Restaurant />}
            <Typography variant="body2" fontWeight="medium">
              {order.order_type?.toUpperCase() || 'PICKUP'}
            </Typography>
          </Box>

          {/* Delivery Address (if applicable) */}
          {order.delivery_address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                {order.delivery_address}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
              onClick={(e) => handleInvoiceDownload(order.unique_string)}
              sx={{
                flex: 1,
                borderColor: '#680000',
                color: '#680000',
                '&:hover': {
                  backgroundColor: '#680000',
                  color: 'white',
                },
                fontSize: '0.75rem',
                py: 0.5,
              }}
            >
              Invoice
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Visibility />}
              onClick={() => handleViewDetails(order)}
              sx={{
                flex: 1,
                backgroundColor: '#680000',
                '&:hover': {
                  backgroundColor: '#8B0000',
                },
                fontSize: '0.75rem',
                py: 0.5,
              }}
            >
              Details
            </Button>
          </Box>
        </Box>
      </Collapse>
    </Card>
  );

  const renderDesktopOrderRow = (order, index) => (
    <Paper
      key={order.id || index}
      elevation={1}
      sx={{
        p: 2,
        border: '1px solid #e0e0e0',
        borderRadius: 2,
        mb: 2,
        '&:hover': {
          boxShadow: 2,
          borderColor: '#680000'
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#680000' }}>
              {order.order_number || `Order #ORD${56789 - index}`}
            </Typography>
            {order.outlet_name && (
              <Chip
                label={order.outlet_name}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#680000',
                  color: '#680000',
                  fontSize: '0.7rem',
                  height: 24
                }}
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {order.ordered_at ? formatDate(order.ordered_at) : `October ${28 - index}, 2023`}
          </Typography>

          {order.menu_items && order.menu_items.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Restaurant sx={{ fontSize: 16, color: '#680000' }} />
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {order.menu_items[0].name}
                {order.menu_items[0].quantity > 1 && ` (x${order.menu_items[0].quantity})`}
                {order.menu_items.length > 1 && ` +${order.menu_items.length - 1} more`}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
            <Chip
              label={statusText[order.order_type?.toLowerCase()] || order.order_type.toUpperCase() || 'Pending'}
              sx={{
                backgroundColor: statusColors[order.order_type?.toLowerCase()] || statusColors.pending,
                color: 'white',
                fontWeight: 'bold',
              }}
              size="small"
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#680000' }}>
              ${order.total_amount || (45.50 - index * 13.5).toFixed(2)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={(e) => handleInvoiceDownload(order.unique_string)}
            sx={{
              borderColor: '#680000',
              color: '#680000',
              '&:hover': {
                backgroundColor: '#680000',
                color: 'white',
              },
            }}
          >
            Invoice
          </Button>
          <Button
            variant="contained"
            startIcon={<Visibility />}
            onClick={() => handleViewDetails(order)}
            sx={{
              backgroundColor: '#680000',
              '&:hover': {
                backgroundColor: '#8B0000',
              },
            }}
          >
            Details
          </Button>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <>
      <Paper sx={{
        width: '100%',
        p: isMobile ? 1.5 : 3,
        backgroundColor: '#fafafa',
        borderRadius: isMobile ? 2 : 3,
        boxShadow: isMobile ? 1 : 2,
      }}>
        {/* Header with Search and Filters */}
        <Box sx={{ mb: 3 }}>
          {/* <Typography variant={isMobile ? "h6" : "h5"} sx={{
            fontWeight: 'bold',
            color: '#680000',
            mb: 2,
            fontSize: isMobile ? '1.25rem' : '1.5rem'
          }}>
            Your Orders
          </Typography> */}

          {/* Search Bar - Always visible */}
          <TextField
            fullWidth
            placeholder="Search orders..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: isMobile ? 20 : 24 }} />
                </InputAdornment>
              ),
            }}
          />

          {/* Mobile Filter Button */}
          {isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setFilterDrawerOpen(true)}
                size="small"
                sx={{
                  borderColor: '#680000',
                  color: '#680000',
                  fontSize: '0.75rem',
                  py: 0.5,
                }}
              >
                Filters
              </Button>
              <Typography variant="caption" color="text.secondary">
                {filteredOrders.length} orders
              </Typography>
            </Box>
          )}

          {/* Desktop Tabs */}
          {!isMobile && (
            <Tabs
              value={statusFilter}
              onChange={handleStatusFilterChange}
              sx={{
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  px: 2,
                  fontSize: '0.875rem'
                },
                '& .Mui-selected': {
                  color: '#680000 !important',
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#680000',
                }
              }}
            >
              <Tab label="All" value="all" />
              {/* <Tab label="Delivery" value="delivery" />
              <Tab label="Pickup" value="pickup" /> */}
            </Tabs>
          )}
        </Box>

        {/* Orders List */}
        <Box sx={{ minHeight: 400 }}>
          {isMobile ? (
            <Box>
              {paginatedOrders.map((order, index) => renderMobileOrderCard(order, index))}
            </Box>
          ) : (
            <Box>
              {paginatedOrders.map((order, index) => renderDesktopOrderRow(order, index))}
            </Box>
          )}
        </Box>

        {/* Mobile Filter Drawer */}
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '70vh',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="bold" color="#680000">
                Filter Orders
              </Typography>
              <IconButton onClick={() => setFilterDrawerOpen(false)}>
                <Close />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
              Order Type
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {['all', 'delivery', 'pickup'].map((type) => (
                <Button
                  key={type}
                  variant={statusFilter === type ? "contained" : "outlined"}
                  onClick={() => handleStatusFilterChange(null, type)}
                  sx={{
                    justifyContent: 'flex-start',
                    textTransform: 'capitalize',
                    backgroundColor: statusFilter === type ? '#680000' : 'transparent',
                    color: statusFilter === type ? 'white' : '#680000',
                    borderColor: '#680000',
                    '&:hover': {
                      backgroundColor: statusFilter === type ? '#8B0000' : 'rgba(104, 0, 0, 0.1)',
                    },
                  }}
                  fullWidth
                >
                  {type === 'all' ? 'All Orders' : type}
                </Button>
              ))}
            </Box>
            <Button
              variant="contained"
              onClick={() => setFilterDrawerOpen(false)}
              sx={{
                mt: 3,
                backgroundColor: '#680000',
                '&:hover': { backgroundColor: '#8B0000' },
                width: '100%',
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Drawer>

        {/* Empty State */}
        {filteredOrders.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ShoppingCart sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography variant={isMobile ? "h6" : "h5"} color="text.secondary" gutterBottom>
              No orders found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try adjusting your search or filter criteria
            </Typography>
          </Box>
        )}

        {/* Pagination */}
        {filteredOrders.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? "small" : "medium"}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 1 : 2}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#680000',
                  borderColor: '#680000',
                },
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#680000',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#8B0000',
                  }
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: 'rgba(104, 0, 0, 0.1)',
                }
              }}
            />
          </Box>
        )}

        {/* Results Count */}
        {filteredOrders.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{
            textAlign: 'center',
            mt: 2,
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}>
            Showing {startIndex + 1}-{Math.min(startIndex + rowsPerPage, filteredOrders.length)} of {filteredOrders.length} orders
          </Typography>
        )}
      </Paper>

      {/* Mobile Order Details Dialog */}
      <Dialog
        fullScreen={isMobile}
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        TransitionComponent={isMobile ? Transition : Fade}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : 16,
            borderRadius: isMobile ? 0 : 2,
            maxWidth: isMobile ? '100%' : 500,
          }
        }}
      >
        {isMobile ? (
          <>
            <AppBar position="sticky" sx={{ bgcolor: '#680000' }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => setDetailsModalOpen(false)}
                  sx={{ mr: 2 }}
                >
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  Order Details
                </Typography>
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ p: 0 }}>
              <OrderDetailsContent
                order={currentOrder}
                isMobile={isMobile}
                handleInvoiceDownload={handleInvoiceDownload}
                onClose={() => setDetailsModalOpen(false)}
              />
            </DialogContent>
          </>
        ) : (
          <>
            <DialogTitle sx={{
              bgcolor: '#680000',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Receipt />
              <Typography variant="h6">Order Details</Typography>
              <IconButton
                aria-label="close"
                onClick={() => setDetailsModalOpen(false)}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <OrderDetailsContent
                order={currentOrder}
                isMobile={isMobile}
                handleInvoiceDownload={handleInvoiceDownload}
                onClose={() => setDetailsModalOpen(false)}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

const OrderDetailsContent = ({ order, isMobile, handleInvoiceDownload, onClose }) => {
  const primaryColor = '#680000';
  console.log("Order => ", typeof order.platform_fee);
  if (!order || Object.keys(order).length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No order details available
        </Typography>
      </Box>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };

  const handleDownloadClick = () => {
    if (order.unique_string) {
      handleInvoiceDownload(order.unique_string);
    }
  };

  return (
    <Box sx={{
      maxHeight: isMobile ? 'calc(100vh - 64px)' : '70vh',
      overflow: 'auto',
    }}>
      {/* Header Info */}
      <Box sx={{display:'flex',backgroundColor: '#fafafa'}}>
      <Box sx={{
        p: isMobile ? 2 : 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ color: primaryColor }}>
            #{order.order_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatDate(order.ordered_at)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            icon={order.order_type?.toLowerCase() === 'delivery' ? <LocalShipping /> : <Store />}
            label={order.order_type?.toUpperCase() || 'PICKUP'}
            sx={{
              backgroundColor: primaryColor,
              color: 'white',
              fontWeight: 'bold'
            }}
            size="small"
          />
          {order.outlet_name && (
            <Chip
              label={order.outlet_name}
              variant="outlined"
              sx={{
                borderColor: primaryColor,
                color: primaryColor,
              }}
              size="small"
            />
          )}
        </Box>
      </Box>
      <Box sx={{
        p: isMobile ? 2 : 3,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fafafa'}}>
        <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between',mb:2}}>
          <Typography variant='h6' sx={{mr:2}}>Order Status : </Typography>
          <Chip 
          label={order.order_status.toUpperCase()}
           sx={{
            color:"#fff",
            backgroundColor:order.order_status === "cancelled" ? "#D32F2F" : order.order_status === "pending" ? "#ff9800" : "#228B22",
            fontWeight: 'bold'
          }}
          />
        </Box>
         <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <Typography variant='h6' sx={{mr:2}}>Payment Status : </Typography>
          <Chip 
          sx={{
            color:"#fff",
            backgroundColor:order.payment_status === "cancelled" ? "#D32F2F" : order.payment_status === "pending" ? "#ff9800" : "#228B22",
            fontWeight: 'bold'
          }}
          label={order.payment_status.toUpperCase()}/>
        </Box>
      </Box>
      </Box>

      {/* Delivery Address */}
      {order.delivery_address && (
        <Box sx={{ p: isMobile ? 2 : 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: primaryColor }}>
            Delivery Address
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocationOn sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">
              {order.delivery_address}
            </Typography>
          </Box>
        </Box>
      )}
      {order.delivery_or_pickup_time && (
        <Box sx={{ p: isMobile ? 2 : 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: primaryColor }}>
            {order.order_type === "delivery" ? "Delivery Date" : "Pickup Date"}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <LocalShipping sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
            <Typography variant="body2">
              {dayjs(order.delivery_or_pickup_time).format("DD MMM YYYY hh:mm A")}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Food Items */}
      <Box sx={{ p: isMobile ? 2 : 3, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: primaryColor }}>
          Food Items ({order.menu_items?.length || 0})
        </Typography>

        <List disablePadding>
          {order.menu_items?.map((item, index) => (
            <React.Fragment key={item.id || index}>
              <ListItem
                disablePadding
                sx={{
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <MenuBook sx={{ fontSize: 16, color: primaryColor }} />
                    <Box sx={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <Typography variant="body2" fontWeight="medium">
                      {item.name}
                    </Typography>
                    {item.size_name && 
                    <Chip sx={{marginLeft:2,bgcolor:"#680000",color: "#fff"}}  label={item.size_name}/> }
                    </Box>
                    
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    Quantity: {item.quantity}
                  </Typography>
                  {item.addons.length > 0 && 
                    item.addons.map((addon,index)=> {
                      return (
                        <Typography>{addon.name} - S${addon.price}</Typography>
                      )
                    })
                  }

                </Box>
                <Typography variant="body2" fontWeight="bold" color={primaryColor}>
                  S${(item.total_price).toFixed(2)}
                </Typography>
              </ListItem>
              {index < order.menu_items.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Order Summary */}
      <Box sx={{ p: isMobile ? 2 : 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2, color: primaryColor }}>
          Order Summary
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2">S${order.subtotal?.toFixed(2)}</Typography>
          </Box>

          {order.tax_amount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Tax</Typography>
              <Typography variant="body2">S${order.tax_amount?.toFixed(2)}</Typography>
            </Box>
          )}

          {order.delivery_fee > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Delivery Fee</Typography>
              <Typography variant="body2">S${order.delivery_fee?.toFixed(2)}</Typography>
            </Box>
          )}

           {order.service_charge> 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Service Charge</Typography>
              <Typography variant="body2">S${order?.service_charge?.toFixed(2)}</Typography>
            </Box>
          )}

           {(order.platform_fee> 0) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Platform Fee</Typography>
              <Typography variant="body2">S${order?.platform_fee}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 1
        }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Total Amount
          </Typography>
          <Typography variant="h6" fontWeight="bold" color={primaryColor}>
            S${order.total_amount?.toFixed(2)}
          </Typography>
        </Box>

        {/* Action Button */}
        {order.unique_string && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<Download />}
            onClick={handleDownloadClick}
            sx={{
              mt: 3,
              backgroundColor: primaryColor,
              '&:hover': { backgroundColor: '#8B0000' },
              py: 1.5,
            }}
          >
            Download Invoice
          </Button>
        )}
      </Box>
    </Box>
  );
};
export default ProfileOrderTable;